export function RefundPolicyPage() {
  return (
    <>
      <header className="nk-header" id="home">
        <div className="section section-lg section-b-0">
          <div className="section-heading">
            <div className="container">
              <div className="row justify-content-center">
                <div className="col-xl-6 col-lg-8">
                  <div className="position-relative text-center">
                    <h2 className="title text-gradient-heading fw-semibold display-6 mb-4">
                      Refund Policy
                    </h2>
                    <p className="fs-8">
                      This Refund Policy describes how Yapamar OÜ
                      (&ldquo;Yapamar&rdquo;, &ldquo;we&rdquo;,
                      &ldquo;us&rdquo;) handles refunds for paid subscriptions
                      to the Yapamar cloud-based marketing intelligence service
                      (the &ldquo;Service&rdquo;).
                    </p>
                    <p className="fs-10 text-muted fw-semibold">
                      Last updated: October 14, 2026
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>
      <main className="nk-main">
        <div className="section section-xl section-t-0">
          <div className="section-content">
            <div className="container">
              <div className="row justify-content-center">
                <div className="col-xl-8">
                  <div className="nk-entry">
                    <h5>1. Scope and Customer Type</h5>
                    <p>
                      The Service is offered exclusively to legal-entity
                      customers (businesses, organisations, and other corporate
                      clients) for professional use. It is not directed at, or
                      sold to, consumers as defined under Regulation (EU)
                      2011/83/EU or any equivalent consumer-protection
                      legislation. The consumer right of withdrawal does not
                      apply.
                    </p>
                    <p>
                      This policy applies to subscription fees and any other
                      charges paid to Yapamar for access to the Service,
                      regardless of payment method or whether payment is
                      processed by Yapamar directly or via a Merchant of Record.
                    </p>

                    <h5>2. No Refunds for Delivered Services</h5>
                    <p>
                      Yapamar provides a digital service that is made available
                      to Customer upon activation of the subscription and
                      continues to be made available for the duration of the
                      subscription term. Because the Service is performed
                      continuously from the start of each billing period,
                      subscription fees are <strong>non-refundable</strong> once
                      the corresponding billing period has commenced.
                    </p>
                    <p>
                      In particular, and except where mandatory law requires
                      otherwise:
                    </p>
                    <ul>
                      <li>
                        No refund is issued for the current billing period if
                        Customer cancels the subscription before its end.
                      </li>
                      <li>
                        No refund is issued for prepaid future billing periods
                        if Customer cancels mid-term (e.g.&nbsp;an annual
                        prepayment is not pro-rated on cancellation).
                      </li>
                      <li>
                        No refund is issued for unused seats, unused model runs,
                        or other unused capacity included in the
                        Customer&rsquo;s plan.
                      </li>
                      <li>
                        No refund is issued for accidental purchase, change of
                        mind, or non-use of the Service after access has been
                        granted.
                      </li>
                    </ul>

                    <h5>3. Cancellation</h5>
                    <p>
                      Customer may cancel the subscription at any time through
                      the in-product account settings or by contacting{' '}
                      <a href="mailto:support@yapamar.com">
                        support@yapamar.com
                      </a>
                      . Cancellation stops the next automatic renewal; access to
                      the Service continues until the end of the then-current
                      billing period. Cancellation does not give rise to a
                      refund of fees already paid.
                    </p>

                    <h5>4. Exceptions Required by Law</h5>
                    <p>
                      Nothing in this Refund Policy excludes or limits any right
                      or remedy that, under the law applicable to the Customer
                      Agreement, cannot be excluded or limited. Where mandatory
                      law obliges Yapamar to issue a refund (for example, in
                      certain jurisdictions, where the Service was not provided
                      at all due to a fault attributable to Yapamar), Yapamar
                      will do so to the minimum extent required by that law.
                    </p>

                    <h5>5. Billing Errors</h5>
                    <p>
                      The following are treated as <em>billing corrections</em>,
                      not refunds, and Yapamar will resolve them on a good-faith
                      basis:
                    </p>
                    <ul>
                      <li>
                        Duplicate charges arising from a payment-processor
                        error.
                      </li>
                      <li>
                        Charges made after a valid cancellation that should have
                        prevented the renewal.
                      </li>
                      <li>
                        Charges issued in the wrong amount due to an internal
                        configuration error on Yapamar&rsquo;s side.
                      </li>
                    </ul>
                    <p>
                      Where a billing error is confirmed, Yapamar will reverse
                      or credit the affected charge without applying this Refund
                      Policy&rsquo;s non-refundable position.
                    </p>

                    <h5>6. How to Request a Refund or Correction</h5>
                    <p>
                      Customer should send refund or billing-correction requests
                      to{' '}
                      <a href="mailto:support@yapamar.com">
                        support@yapamar.com
                      </a>{' '}
                      and include:
                    </p>
                    <ul>
                      <li>The registered account email and company name.</li>
                      <li>
                        The invoice number or transaction reference of the
                        charge in question.
                      </li>
                      <li>
                        A brief description of the reason for the request.
                      </li>
                    </ul>
                    <p>
                      Yapamar will acknowledge the request within five (5)
                      business days and aim to resolve it within fifteen (15)
                      business days. Where a refund is granted, it will be
                      issued to the original payment method used for the charge.
                    </p>

                    <h5>7. Modification</h5>
                    <p>
                      Yapamar may amend this Refund Policy from time to time.
                      Material changes will take effect for billing periods that
                      begin after the change is published. The version
                      applicable to any given charge is the version in force on
                      the date the charge was incurred.
                    </p>

                    <h5>8. Contact</h5>
                    <p>Yapamar OÜ</p>
                    <p className="d-flex align-items-center gap-2 mb-1">
                      <em className="icon ni ni-mail" />
                      <a href="mailto:support@yapamar.com">
                        support@yapamar.com
                      </a>
                    </p>
                    <p className="d-flex align-items-center gap-2">
                      <em className="icon ni ni-map-pin" />
                      Sepapaja 6, Tallinn 15551, Estonia
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}
